import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "@sdcp/ui";

import type { Challenge } from "@sdcp/api-client";

export interface GateProps {
  challenge: Challenge;
  onSubmit: (answer: string) => void;
  /** Ask for an emailed code. Only used when the challenge is one. */
  onSendCode?: () => void;
  busy?: boolean;
  sending?: boolean;
  error?: string | null;
}

/**
 * The gate between a link and a session.
 *
 * Deliberately bare. Nothing about the borrower, the lender, or what is being
 * asked for is on this screen, because the server has not sent any of it — a
 * link that leaks should reveal that it is a PaperVerity link and nothing else.
 * The one exception is the masked address a code goes to, which a borrower
 * needs in order to know which inbox to open.
 *
 * The factor is whatever the server says it is: an emailed code for every link
 * issued since email became required (ADR-0004), a date of birth for older ones.
 */
export function Gate(props: GateProps) {
  if (props.challenge.kind === "email_otp") return <EmailCodeGate {...props} />;
  return <FactGate {...props} />;
}

/** The frame both factors share: the wordmark and nothing about anyone. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <main className="mx-auto flex h-dvh max-w-[430px] flex-col justify-center gap-6 bg-bg-app px-6">
      <header className="flex flex-col items-center gap-2 text-center">
        <span className="text-2xl font-extrabold tracking-tight">
          Paper<span className="text-verity-600">Verity</span>
        </span>
      </header>
      {children}
    </main>
  );
}

/** A fact the borrower knows — date of birth, on links issued before codes. */
function FactGate({ challenge, onSubmit, busy = false, error }: GateProps) {
  const [answer, setAnswer] = useState("");
  const isDate = challenge.kind === "date_of_birth";
  const last = challenge.attempts_remaining <= 1;

  return (
    <Shell>
      <form
        className="flex flex-col gap-4 rounded-xl border border-border-cool bg-surface p-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (answer.trim()) onSubmit(answer.trim());
        }}
      >
        <label htmlFor="gate-answer" className="flex flex-col gap-1">
          <span className="text-[17px] font-bold">{challenge.prompt}</span>
          {challenge.hint && (
            <span className="text-sm text-ink-500">{challenge.hint}</span>
          )}
        </label>

        <input
          id="gate-answer"
          // A date input gives a picker on a phone and submits ISO, which is
          // the format the server parses first. The server also accepts what
          // people type, so a browser without the picker still works.
          type={isDate ? "date" : "text"}
          inputMode={isDate ? undefined : "numeric"}
          autoComplete={isDate ? "bday" : "one-time-code"}
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          className="rounded-lg border border-border px-3 py-3 text-base"
        />

        {error && (
          <p role="alert" className="text-sm text-err-600">
            {error}
          </p>
        )}

        {/* Only when it is nearly gone. A counter shown from the first attempt
            reads as a threat to someone who has done nothing wrong. */}
        {last && !error && (
          <p className="text-sm text-warn-600">
            {challenge.attempts_remaining === 0
              ? "No tries left — ask your loan officer for a new link."
              : "One try left."}
          </p>
        )}

        {/* Submits through the form, with no `onClick` of its own. A `<button>`
            inside a form is `type="submit"` unless told otherwise, so an
            `onClick` here fired *as well as* the form handler — one tap, two
            POSTs, two of the three attempts gone. A borrower who mistyped once
            would be locked out on their second tap. */}
        <Button type="submit" disabled={busy || !answer.trim()}>
          {busy ? "Checking…" : "Continue"}
        </Button>
      </form>
    </Shell>
  );
}

/**
 * An emailed one-time code (ADR-0004).
 *
 * Two steps on one screen. First a button, because opening a link must not
 * send mail by itself — a mail scanner that fetches every link would otherwise
 * trigger a code nobody asked for. Then one six-digit box: a single input
 * rather than six, so paste, the keyboard's code suggestion, and screen readers
 * all work, and it submits itself on the last digit.
 */
function EmailCodeGate({
  challenge,
  onSubmit,
  onSendCode,
  busy = false,
  sending = false,
  error,
}: GateProps) {
  const length = challenge.code_length ?? 6;
  const [code, setCode] = useState("");
  // Keyed on the challenge object: each send returns a new one, so the timer
  // restarts even when the server names the same number of seconds.
  const wait = useCountdown(challenge.resend_after_seconds ?? 0, challenge);
  const input = useRef<HTMLInputElement>(null);
  const sent = challenge.code_sent ?? false;

  // A fresh code means a fresh box: clear what was typed against the old one,
  // and put the cursor where the next keystroke belongs.
  useEffect(() => {
    if (!sent) return;
    setCode("");
    input.current?.focus();
  }, [sent, challenge]);

  const submit = (value: string) => {
    if (value.length === length && !busy) onSubmit(value);
  };

  const resendLabel = sending
    ? "Sending…"
    : wait > 0
      ? `Resend code in ${clock(wait)}`
      : "Resend code";

  if (!sent)
    return (
      <Shell>
        <section className="flex flex-col gap-4 rounded-xl border border-border-cool bg-surface p-5">
          <div className="flex flex-col gap-1">
            <h1 className="text-[17px] font-bold">{challenge.prompt}</h1>
            <p className="text-sm text-ink-500">
              We'll email a {length}-digit code to{" "}
              <span className="font-medium text-ink-900">
                {challenge.destination}
              </span>
              . Enter it here to open your documents.
            </p>
          </div>

          {error && (
            <p role="alert" className="text-sm text-err-600">
              {error}
            </p>
          )}

          <Button
            type="button"
            onClick={onSendCode}
            disabled={sending || wait > 0}
          >
            {sending
              ? "Sending…"
              : wait > 0
                ? `Email me a code (${clock(wait)})`
                : "Email me a code"}
          </Button>
        </section>
      </Shell>
    );

  return (
    <Shell>
      <form
        className="flex flex-col gap-4 rounded-xl border border-border-cool bg-surface p-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit(code);
        }}
      >
        <label htmlFor="gate-code" className="flex flex-col gap-1">
          <span className="text-[17px] font-bold">Enter your code</span>
          <span className="text-sm text-ink-500">
            We sent a {length}-digit code to{" "}
            <span className="font-medium text-ink-900">
              {challenge.destination}
            </span>
            . It expires in 10 minutes.
          </span>
        </label>

        <input
          id="gate-code"
          ref={input}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern={`[0-9]{${length}}`}
          maxLength={length}
          placeholder={"•".repeat(length)}
          aria-describedby={error ? "gate-error" : undefined}
          aria-invalid={Boolean(error)}
          value={code}
          onChange={(e) => {
            // Digits only, whatever was pasted — "123 456" and "123-456" are
            // how codes get copied out of an email.
            const digits = e.target.value.replace(/\D/g, "").slice(0, length);
            setCode(digits);
            if (digits.length === length) submit(digits);
          }}
          className="rounded-lg border border-border px-3 py-3 text-center font-mono text-2xl tracking-[0.5em]"
        />

        {error && (
          <p id="gate-error" role="alert" className="text-sm text-err-600">
            {error}
          </p>
        )}

        {/* Submits through the form, never an onClick as well — see FactGate. */}
        <Button type="submit" disabled={busy || code.length !== length}>
          {busy ? "Checking…" : "Verify"}
        </Button>

        <div className="flex flex-col items-center gap-1 text-sm text-ink-500">
          <span>Didn't get it? Check your spam folder.</span>
          <button
            type="button"
            onClick={onSendCode}
            disabled={sending || wait > 0}
            className="font-medium text-verity-600 disabled:text-ink-400"
          >
            {resendLabel}
          </button>
        </div>
      </form>
    </Shell>
  );
}

/** Seconds left, counting down from what the server said. Restarts whenever
 * the server says something new. */
function useCountdown(from: number, key: unknown): number {
  const [left, setLeft] = useState(from);
  useEffect(() => {
    setLeft(from);
    if (from <= 0) return;
    const started = Date.now();
    const timer = window.setInterval(() => {
      const remaining = Math.max(
        0,
        from - Math.floor((Date.now() - started) / 1000),
      );
      setLeft(remaining);
      if (remaining === 0) window.clearInterval(timer);
    }, 250);
    return () => window.clearInterval(timer);
  }, [from, key]);
  return left;
}

function clock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}
